// src/components/Steps/SessionSummary/SessionSummary.jsx
import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { IoMdCheckmark } from 'react-icons/io';
import { ActionButtonWrapper, EvHeader, LabelValueRow, InfoPanel, Notice } from '../..';
import BackendSessionSummary from '../../sessions/BackendSessionSummary';
import { useSession } from '../../../context/SessionContext';
import { useToast } from '../../../context/ToastContext';
import { getReceiptInfo, sendReceiptEmail, hasReceiptBeenSent } from '../../../services/receipts.service';
import { getCustomerSession, submitSessionSummary, markFreeSessionAsPaid, retriggerFinalization } from '../../../api/sessionsApi';
import { fetchStationById } from '../../../api/fetch';
import { formatCurrency } from '../../../utils/helpers';
import { STEPS } from '../../../constants/steps';
import RatingModal from './RatingModal/RatingModal';
import { resolveReceiptContact, getReceiptDisplayInfo } from '../../../utils/receiptContact';
import { getFinalSessionEnergy, formatEnergyKwh } from '../../../utils/sessionEnergy';
import { computeReceiptBreakdown } from './computeReceiptBreakdown.js';
import { cardSummaryPaymentLabel, serverCapturedCard } from '../../../utils/statusCopy';
import { resolveBillingDisplay, offersCustomerReceipt } from '../../../utils/billingDisplay';
import BillingDisplayRows from '../../domain/BillingDisplayRows/BillingDisplayRows';
import CanonicalSessionPanel from '../../domain/CanonicalSessionPanel/CanonicalSessionPanel';
import { useCanonicalSession } from '../../../chargerUx/useCanonicalSession';
import { presentSnapshot } from '../../../chargerUx/copy';
import { LuTriangleAlert } from 'react-icons/lu';
import './SessionSummary.less';

const AUTO_EXIT_SECONDS = 30;

/**
 * Idempotency tracking for payment capture
 * Prevents multiple capture attempts for the same session
 */

/**
 * Single source of truth for "backend has produced enough to send a receipt".
 *
 * Historically we gated on `finalized_at` / `is_finalized` only. In practice
 * the backend frequently parks sessions at `status='completed'` (or
 * 'stopped') with full cost + ended_at populated but never sets
 * `finalized_at` — leaving the auto-send loop spinning forever on
 * "Waiting for data to hydrate...". This helper treats a backend-terminal
 * status as ready when the essential receipt fields are present, so the
 * three places that consume this signal (Effect A capture trigger,
 * Effect B polling exit, isReceiptDataReady) can never drift apart.
 */
const isSessionTerminalReady = (sess) => {
  if (!sess) return false;
  if (sess.finalized_at || sess.is_finalized === true) return true;

  const status = String(sess.status || '').toLowerCase();
  const terminalStatus = status === 'finalized' || status === 'completed' || status === 'stopped';
  if (!terminalStatus) return false;

  const hasCost =
    sess.final_total_cost != null ||
    sess.summary?.total_cost != null ||
    sess.summary_total_cost != null;
  const hasEndedAt = !!(sess.stopped_at || sess.summary?.ended_at || sess.summary_ended_at);

  return hasCost && hasEndedAt;
};

const SessionSummary = () => {
  const { state, reset, setStep, setStationMeta } = useSession();
  // Charger UX: when the API says the session did not simply complete (ended
  // early, safety stop, failed to start…), this screen says so instead of
  // "Charging completed". Without a canonical snapshot it is unchanged.
  const { snapshot: canonical } = useCanonicalSession(state?.sessionUid);
  const outcome = canonical?.terminal === true && canonical.sessionState !== 'COMPLETED'
    ? presentSnapshot(canonical) : null;
  const summaryTitle = outcome ? outcome.heading : 'Charging completed';
  const summaryIcon = outcome
    ? <div className="session-summary-status-circle is-attention"><LuTriangleAlert size={34} aria-hidden="true" /></div>
    : <div className="session-summary-status-circle"><IoMdCheckmark size={40} /></div>;

  // A cold resume (code recovery in a fresh browser, Safari purging the tab)
  // restores the session from the server, which carries only station_id --
  // the charger's name and location live in the station record. Fetch them
  // so the summary does not show "—". Display-only: pricing stays with the
  // session's own pricing snapshot.
  useEffect(() => {
    if (!state.stationId || state.stationMeta?.name) return undefined;
    let cancelled = false;
    fetchStationById(state.stationId)
      .then((res) => {
        if (cancelled || !res?.ok) return;
        const vd = res.verified_data || {};
        const nd = res.network_data || {};
        setStationMeta?.({
          ...(state.stationMeta || {}),
          name: vd.display_name || nd.display_name || state.stationId,
          location: vd.location_text || null,
        });
      })
      .catch(() => { /* the row keeps its "—" placeholder */ });
    return () => { cancelled = true; };
  }, [state.stationId, state.stationMeta?.name]); // eslint-disable-line react-hooks/exhaustive-deps
  const { showToast } = useToast();
  
  const [captureStatus, setCaptureStatus] = useState('idle'); // 'idle' | 'capturing' | 'captured' | 'failed'
  const [capturedAmount, setCapturedAmount] = useState(null);
  const [receiptStatus, setReceiptStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'failed'
  const [countdown, setCountdown] = useState(AUTO_EXIT_SECONDS);
  const [receiptEmail, setReceiptEmail] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  // Get Receipt: the sheet being open pauses the auto-exit, and a successful
  // request replaces the button so it cannot be re-submitted from here.
  const [receiptRequested, setReceiptRequested] = useState(false);
  const captureOpenRef = useRef(false);
  const handleCaptureOpenChange = useCallback((open) => { captureOpenRef.current = open; }, []);
  const handleReceiptRequested = useCallback(() => setReceiptRequested(true), []);
  
  // Server session state - SOURCE OF TRUTH for finalized values
  const [serverSession, setServerSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  
  // Timeout state for graceful degradation
  const [preparingTimedOut, setPreparingTimedOut] = useState(false);
  const preparingTimeoutRef = useRef(null);
  
  const countdownTimerRef = useRef(null);
  const autoSendTimeoutRef = useRef(null);
  // Track which sessionUid we've auto-sent for (allows re-send if sessionUid changes)
  const autoSentForSessionRef = useRef(null);
  // Dedup signature for the "Waiting for data to hydrate..." log so it
  // fires once per change in the missing-field set rather than every poll.
  const hydrationLogRef = useRef(null);
  
  // Fetch session from server on mount to get authoritative summary data
  useEffect(() => {
    const fetchSession = async () => {
      if (!state.sessionUid) {
        console.log('[SessionSummary] No sessionUid - skipping server fetch');
        setLoadingSession(false);
        return;
      }
      
      console.log('[SessionSummary] 📊 Fetching server session:', state.sessionUid);
      
      try {
        const result = await getCustomerSession(state.sessionUid);
        
        if (result.ok && result.session) {
          console.log('[SessionSummary] ✅ Server session loaded:', {
            final_total_cost: result.session.final_total_cost,
            summary_total_cost: result.session.summary?.total_cost,
            payment_amount: result.session.payment?.payment_amount,
            final_energy_kwh: result.session.final_energy_kwh,
            finalized_at: result.session.finalized_at,
            stopped_at: result.session.stopped_at,
            status: result.session.status
          });
          setServerSession(result.session);
        } else {
          console.warn('[SessionSummary] ⚠️ Failed to fetch server session:', result.error);
        }
      } catch (error) {
        console.error('[SessionSummary] ❌ Error fetching server session:', error);
      } finally {
        setLoadingSession(false);
      }
    };
    
    fetchSession();
  }, [state.sessionUid]);
  
  // ============================================================
  // Get final session energy using utility (handles all fallback logic)
  // Prefers API summary_energy_kwh, falls back to live energyKWh
  // Never shows cumulative meter - always per-session delta
  // ============================================================
  const energyDeliveredKWh = getFinalSessionEnergy(state, serverSession);
  
  // Calculate duration from server summary (preferred) or client state (fallback)
  const duration = useMemo(() => {
    // Try server finalized duration first, then nested summary, then flat legacy field
    const durationSec = serverSession?.final_duration_seconds
      || serverSession?.summary?.duration_seconds
      || serverSession?.summary_duration_seconds;
    if (durationSec) {
      const totalMinutes = Math.floor(durationSec / 60);
      const hours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;

      if (hours > 0) {
        return `${hours}h ${remainingMinutes}m`;
      }
      return `${totalMinutes}m`;
    }

    // Fallback to calculating from timestamps
    const startTime = serverSession?.summary?.started_at ?? serverSession?.summary_started_at ?? state.live?.startTime;
    const endTime = serverSession?.summary?.ended_at ?? serverSession?.summary_ended_at ?? state.live?.endTime;
    
    if (!startTime || !endTime) return '—';
    
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const durationMs = end - start;
      const minutes = Math.floor(durationMs / 1000 / 60);
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      if (hours > 0) {
        return `${hours}h ${remainingMinutes}m`;
      }
      return `${minutes}m`;
    } catch (error) {
      console.error('[SummaryExit] Error calculating duration:', error);
      return '—';
    }
  }, [serverSession?.summary_duration_seconds, serverSession?.summary_started_at, serverSession?.summary_ended_at, state.live?.startTime, state.live?.endTime]);

  // Format times - prefer server summary, fallback to client state
  const startedTime = useMemo(() => {
    const timestamp = serverSession?.summary?.started_at ?? serverSession?.summary_started_at ?? serverSession?.started_at ?? state?.live?.startTime;
    if (!timestamp) return '—';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return '—';
    }
  }, [serverSession?.summary_started_at, serverSession?.started_at, state?.live?.startTime]);

  const endedTime = useMemo(() => {
    const timestamp = serverSession?.summary?.ended_at ?? serverSession?.summary_ended_at ?? serverSession?.stopped_at ?? state?.live?.endTime;
    if (!timestamp) return '—';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return '—';
    }
  }, [serverSession?.summary_ended_at, serverSession?.stopped_at, state?.live?.endTime]);

  // ============================================================
  // FINALIZATION STATUS: Determines whether backend has completed
  // the finalization pipeline (energy, cost, payment capture).
  // CRITICAL for preventing display of false zeros on summary.
  // ============================================================
  const isSessionFinalized = useMemo(() => {
    return !!(serverSession?.finalized_at || serverSession?.is_finalized === true || serverSession?.status === 'finalized');
  }, [serverSession?.finalized_at, serverSession?.is_finalized, serverSession?.status]);

  // ============================================================
  // RECORDED COST — the only amount this screen may print when the
  // session carries no `billing`. Server values only; `null` until the
  // server has one. Nothing here is multiplied in the client.
  //
  // Only trust a server cost when either:
  //   a) the value is > 0 (clearly real, not a placeholder), OR
  //   b) the session is finalized, so a server 0 is authoritative.
  // Before finalization the backend may hold final_total_cost=0 as a
  // default, and printing it would be a false zero.
  // ============================================================
  const recordedCost = useMemo(() => {
    const candidates = [
      serverSession?.final_total_cost,   // set by server-side finalizeSession
      serverSession?.summary?.total_cost, // nested summary shape
      serverSession?.summary_total_cost,  // legacy flat field
    ];
    for (const raw of candidates) {
      if (raw == null || raw === '') continue;
      const value = Number(raw);
      if (!Number.isFinite(value)) continue;
      if (value > 0 || isSessionFinalized) return value;
    }
    return null;
  }, [serverSession?.final_total_cost, serverSession?.summary?.total_cost, serverSession?.summary_total_cost, isSessionFinalized]);

  // ============================================================
  // SUBMITTED COST — NOT DISPLAYED. `POST /sessions/:uid/summary` takes
  // a `totalCost` from the client when the backend has not finalized
  // (see sendReceipt). That payload keeps the estimate it always
  // carried (mirrors backend computeFinalCostWithMinCharge); the server
  // decides what to do with it. No row on this screen reads it.
  // ============================================================
  const BILLING_UNIT = state?.pricingSnapshot?.billing_unit || state?.stationMeta?.billingUnit || 'kwh';
  const RATE_PER_KWH = state?.pricingSnapshot?.rate_per_kwh || state?.stationMeta?.ratePerKwh || 0.25;
  const RATE_PER_MINUTE = state?.pricingSnapshot?.rate_per_minute || state?.stationMeta?.ratePerMinute || 0;
  const SESSION_FEE = state?.pricingSnapshot?.session_fee || state?.stationMeta?.sessionFee || 0;
  const MIN_CHARGE = state?.pricingSnapshot?.min_charge_amount || state?.stationMeta?.minChargeAmount || 0;
  const submittedTotalCost = useMemo(() => {
    if (recordedCost != null) return recordedCost;
    let computed;
    if (BILLING_UNIT === 'minute') {
      const durationSec = state?.live?.startTime && state?.live?.endTime
        ? Math.floor((new Date(state.live.endTime) - new Date(state.live.startTime)) / 1000)
        : 0;
      computed = SESSION_FEE + ((durationSec / 60) * RATE_PER_MINUTE);
    } else {
      computed = (energyDeliveredKWh * RATE_PER_KWH) + SESSION_FEE;
    }
    if (MIN_CHARGE > 0 && computed < MIN_CHARGE) {
      computed = MIN_CHARGE;
    }
    return Math.round(computed * 100) / 100;
  }, [recordedCost, energyDeliveredKWh, RATE_PER_KWH, RATE_PER_MINUTE, BILLING_UNIT, SESSION_FEE, MIN_CHARGE, state?.live?.startTime, state?.live?.endTime]);

  /**
   * MEMOIZED receipt contact resolution
   * Resolves ONCE when relevant fields change, not on every render
   * 
   * Resolution order:
   * 1. Explicit receipt info entered in the flow
   * 2. Verified vehicle owner contact from registry
   * 3. Legacy localStorage
   * 
   * ✅ FIX: Narrow dependencies to only the fields needed for receipt resolution
   * This prevents repeated resolution on every state update (e.g., countdown timer)
   */
  const resolvedReceiptContact = useMemo(() => {
    // Use receipt contact resolver with fallback chain
    const contact = resolveReceiptContact(state);
    
    if (contact) {
      console.log('[Receipt] Resolved contact:', {
        email: contact.email,
        source: contact.source,
        hasName: !!contact.fullName
      });
      return {
        email: contact.email,
        fullName: contact.fullName,
        source: contact.source
      };
    }

    // Fallback to localStorage (legacy compatibility)
    const storageKey = state.sessionUid || state.stationId || 'current';
    const storedInfo = getReceiptInfo(storageKey);
    if (storedInfo?.email) {
      console.log('[Receipt] Found email in localStorage (legacy):', storedInfo.email);
      return {
        email: storedInfo.email,
        fullName: storedInfo.fullName,
        source: 'localStorage'
      };
    }

    console.warn('[Receipt] No email found in any source');
    return null;
  }, [
    // NARROW DEPENDENCIES: Only the fields actually used for receipt resolution
    state.receiptInfo?.email,
    state.receiptInfo?.fullName,
    state.vehicle?.verified,
    state.vehicle?.ownerEmail,
    state.vehicle?.ownerName,
    state.sessionUid,
    state.stationId
  ]);

  /**
   * MEMOIZED receipt display info
   * Prevents repeated calls to resolveReceiptContact during countdown timer renders
   * 
   * ✅ FIX: Memoize getReceiptDisplayInfo result to stop render loop spam
   * Dependencies: Only the resolved contact fields (already memoized above)
   */
  const receiptDisplayInfo = useMemo(() => {
    if (!resolvedReceiptContact || !resolvedReceiptContact.email) {
      return {
        hasContact: false,
        message: 'No receipt email on file.',
        email: null,
        source: null
      };
    }

    // Format message based on source
    let message;
    switch (resolvedReceiptContact.source) {
      case 'explicit':
        message = `Receipt will be sent to ${resolvedReceiptContact.email}`;
        break;
      case 'vehicle_registry':
        message = `Receipt will be sent to ${resolvedReceiptContact.email} (vehicle owner)`;
        break;
      case 'legacy':
        message = `Receipt will be sent to ${resolvedReceiptContact.email}`;
        break;
      default:
        message = `Receipt will be sent to ${resolvedReceiptContact.email}`;
    }

    return {
      hasContact: true,
      message,
      email: resolvedReceiptContact.email,
      fullName: resolvedReceiptContact.fullName,
      source: resolvedReceiptContact.source
    };
  }, [resolvedReceiptContact]);

  /**
   * Determine if backend-finalized receipt is ready (for AUTO-SEND only)
   * This requires full backend summary hydration
   * ⚠️ MUST BE DECLARED EARLY - used by multiple useEffects below
   */
  const isReceiptDataReady = useMemo(() => {
    if (!state.sessionUid) {
      console.log('[Receipt] ❌ Not ready: missing sessionUid');
      return false;
    }
    // On cold recovery stationMeta may not be populated — stationId alone is sufficient
    if (!state.stationMeta?.name && !state.stationId) {
      console.log('[Receipt] ❌ Not ready: missing station identifier (no stationMeta or stationId)');
      return false;
    }
    if (!resolvedReceiptContact?.email) {
      console.log('[Receipt] ❌ Not ready: missing email');
      return false;
    }
    const paymentRequired = state.vehicle?.paymentRequired !== false;
    const billingMode = state.vehicle?.billingMode || 'PUBLIC_CARD';
    if (paymentRequired) {
      if (captureStatus !== 'captured') {
        console.log(`[Receipt] ⏳ Not ready: payable session (${billingMode}) not yet captured, status: ${captureStatus}`);
        return false;
      }
    } else {
      if (captureStatus !== 'not_required' && captureStatus !== 'captured') {
        console.log(`[Receipt] ⏳ Not ready: non-payment billing mode (${billingMode}) still processing, status: ${captureStatus}`);
        return false;
      }
    }
    
    // Check AUTHORITATIVE backend finalization signals.
    // Treat backend-terminal-with-data as ready too — the backend often
    // parks sessions at status='completed' (cost + ended_at present) and
    // never flips finalized_at. Gating on finalized_at alone produced
    // infinite "Waiting for data to hydrate..." spam.
    const backendFinalized = isSessionTerminalReady(serverSession);

    if (!backendFinalized) {
      // GRACEFUL DEGRADATION: If finalization polling timed out (preparingTimedOut=true),
      // allow receipt to proceed with available data. This handles edge cases where:
      // - Session ended before charging started (stuck in 'preparing')
      // - Backend finalization is slow or failed silently
      // - Network issues prevent polling from detecting finalization
      //
      // Phase 4 / F4: degraded mode is permitted ONLY for non-payment-required
      // billing modes (FREE / FLEET_INVOICE). For PUBLIC_CARD / DISCOUNTED_CARD
      // the backend MUST confirm finalization (and payment capture) — otherwise
      // we'd send a receipt for an unpaid charge. The 30s/90s timeouts no longer
      // force captureStatus='captured' for payment-required modes, so this guard
      // is defense in depth.
      if (preparingTimedOut && !paymentRequired) {
        console.warn('[Receipt] ⚠️ Backend not finalized but proceeding (FREE/FLEET timeout degraded mode)', {
          finalized_at: serverSession?.finalized_at,
          status: serverSession?.status,
          captureStatus,
          preparingTimedOut,
          billingMode
        });
        // Fall through — allow receipt with available data
      } else if (preparingTimedOut && paymentRequired) {
        console.warn(`[Receipt] ❌ Not ready for AUTO-SEND: ${billingMode} requires backend finalization; degraded mode disallowed`, {
          finalized_at: serverSession?.finalized_at,
          status: serverSession?.status,
          paymentStatus: serverSession?.payment?.status || serverSession?.payment_status,
        });
        return false;
      } else {
        console.log('[Receipt] ⏳ Not ready for AUTO-SEND: backend not finalized yet', {
          finalized_at: serverSession?.finalized_at,
          is_finalized: serverSession?.is_finalized,
          hasSummaryData: !!(serverSession?.summary?.total_cost && serverSession?.summary?.ended_at),
          loadingSession,
          billingMode,
          preparingTimedOut
        });
        return false;
      }
    }
    console.log(`[Receipt] ✅ Ready for AUTO-SEND: ${paymentRequired ? 'payment captured' : 'non-payment mode'} (${billingMode})`, {
      totalCost: serverSession?.final_total_cost || serverSession?.summary?.total_cost,
      endedAt: serverSession?.summary?.ended_at || serverSession?.stopped_at,
      energyKwh: energyDeliveredKWh,
      paymentRequired,
      degradedMode: !backendFinalized
    });
    return true;
  }, [
    state.sessionUid,
    state.stationMeta?.name,
    state.vehicle?.paymentRequired,
    state.vehicle?.billingMode,
    resolvedReceiptContact?.email,
    captureStatus,
    serverSession?.finalized_at,
    serverSession?.is_finalized,
    serverSession?.status,
    serverSession?.payment_status,
    serverSession?.payment?.status,
    // Fields read by isSessionTerminalReady — needed so the memo recomputes
    // when the completed-with-data path qualifies as terminal.
    serverSession?.final_total_cost,
    serverSession?.summary?.total_cost,
    serverSession?.summary_total_cost,
    serverSession?.stopped_at,
    serverSession?.summary?.ended_at,
    serverSession?.summary_ended_at,
    energyDeliveredKWh,
    loadingSession,
    preparingTimedOut
  ]);

  /**
   * ✅ FIXED: Determine if MANUAL receipt send is allowed
   * 
   * Manual send uses UI-hydrated summary data and does NOT require backend finalization.
   * This allows users to manually send receipts even if backend finalization is delayed.
   * 
   * Manual send is allowed when ALL of these are true:
   * 1. receipt email exists
   * 2. sessionUid and station metadata exist
   * 3. payment is captured/paid (or not required)
   * 4. UI has visible summary data (energyKWh, endTime)
   * 
   * Does NOT require:
   * - backend finalized_at / is_finalized
   * - backend summary_* fields
   * 
   * The backend will use whatever summary data is available when sending.
   */
  const canManualSendReceipt = useMemo(() => {
    // 1. Must have receipt email
    if (!resolvedReceiptContact?.email) {
      console.log('[Manual Send] ❌ Cannot send: missing email');
      return false;
    }
    
    // 2. Must have sessionUid and a station identifier
    // On cold recovery stationMeta may not be re-populated — stationId is sufficient
    if (!state.sessionUid || (!state.stationMeta?.name && !state.stationId)) {
      console.log('[Manual Send] ❌ Cannot send: missing session or station data');
      return false;
    }
    
    // 3. Payment must be captured/paid OR not required
    const paymentRequired = state.vehicle?.paymentRequired !== false;
    const billingMode = state.vehicle?.billingMode || 'PUBLIC_CARD';
    
    if (paymentRequired) {
      if (captureStatus !== 'captured') {
        console.log(`[Manual Send] ❌ Cannot send: payment not captured (${billingMode}), status: ${captureStatus}`);
        return false;
      }
    } else {
      if (captureStatus !== 'not_required' && captureStatus !== 'captured') {
        console.log(`[Manual Send] ❌ Cannot send: billing mode (${billingMode}) still processing, status: ${captureStatus}`);
        return false;
      }
    }
    
    // 4. Must have summary data to send receipt
    const isFreeSession = billingMode === 'FREE';
    const backendIsFinalized = !!(
      serverSession?.finalized_at ||
      serverSession?.is_finalized === true ||
      serverSession?.status === 'finalized'
    );

    // Phase 4 / F4: PUBLIC_CARD / DISCOUNTED_CARD must NEVER manual-send unless
    // the backend has confirmed both finalization and payment capture, and the
    // session is not in a blocking state. Pre-fix, this branch allowed a manual
    // send purely on UI-hydrated `energyKWh + endTime`, which let degraded mode
    // bypass payment validation. The backend correctly refused, but the gate
    // must hold on the frontend too.
    if (paymentRequired) {
      if (!backendIsFinalized) {
        console.log(`[Manual Send] ❌ ${billingMode} requires backend finalization (finalized_at/is_finalized)`, {
          finalized_at: serverSession?.finalized_at,
          is_finalized: serverSession?.is_finalized,
          status: serverSession?.status,
        });
        return false;
      }

      const backendPaymentStatus = serverSession?.payment?.status || serverSession?.payment_status;
      const validPaymentStatuses = ['captured', 'paid', 'bypassed'];
      if (!validPaymentStatuses.includes(backendPaymentStatus)) {
        console.log(`[Manual Send] ❌ ${billingMode} requires payment_status in {captured,paid,bypassed} (got: ${backendPaymentStatus})`);
        return false;
      }

      const blockedBackendStatuses = ['expired', 'cancelled', 'failed', 'preparing', 'pending'];
      const backendStatus = serverSession?.status;
      if (blockedBackendStatuses.includes(backendStatus)) {
        console.log(`[Manual Send] ❌ ${billingMode} cannot send while backend status=${backendStatus}`);
        return false;
      }

      console.log(`[Manual Send] ✅ ${billingMode} fully validated: backend finalized, payment ${backendPaymentStatus}, status ${backendStatus}`, {
        email: resolvedReceiptContact.email,
        energyKWh: energyDeliveredKWh,
      });
      return true;
    }

    // FREE / FLEET_INVOICE (paymentRequired === false): keep the existing
    // UI-hydrated summary path. These modes do not depend on payment capture.
    const hasUISummaryData = !!(
      (energyDeliveredKWh > 0 || isFreeSession || backendIsFinalized) &&
      (state.live?.endTime || serverSession?.stopped_at || serverSession?.ended_at)
    );

    if (!hasUISummaryData) {
      console.log('[Manual Send] ❌ Cannot send: missing summary data', {
        energyKWh: energyDeliveredKWh,
        hasEndTime: !!state.live?.endTime,
        isFreeSession,
        backendFinalized: backendIsFinalized,
        billingMode
      });
      return false;
    }

    console.log(`[Manual Send] ✅ Can send: using UI-hydrated summary data (${billingMode})`, {
      email: resolvedReceiptContact.email,
      energyKWh: energyDeliveredKWh,
      endTime: state.live?.endTime,
      paymentRequired,
      captureStatus,
      hasBackendSummary: !!(serverSession?.summary_total_cost && serverSession?.summary_ended_at)
    });
    return true;
  }, [
    resolvedReceiptContact?.email,
    state.sessionUid,
    state.stationMeta?.name,
    state.vehicle?.paymentRequired,
    state.vehicle?.billingMode,
    state.live?.endTime,
    captureStatus,
    energyDeliveredKWh,
    serverSession?.finalized_at,
    serverSession?.is_finalized,
    serverSession?.status,
    serverSession?.payment_status,
    serverSession?.payment?.status,
    serverSession?.stopped_at,
    serverSession?.ended_at,
    serverSession?.summary_total_cost,
    serverSession?.summary_ended_at
  ]);

  /**
   * Reset the inactivity countdown timer
   */
  const resetCountdown = useCallback(() => {
    setCountdown(AUTO_EXIT_SECONDS);
  }, []);

  /**
   * Handle user interaction to reset timer
   */
  const handleUserInteraction = useCallback(() => {
    resetCountdown();
  }, [resetCountdown]);

  /**
   * Start a new session - clear state and return to start
   */
  const handleStartNewSession = useCallback(() => {
    console.log('[SummaryExit] Starting new session - clearing state');
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    reset();
    setStep(STEPS.EV_PROFILE);
  }, [reset, setStep]);

  /**
   * Send receipt email (auto or manual)
   * Enhanced with detailed logging and validation
   * 
   * CRITICAL FIX FOR FREE SESSIONS:
   * - FREE sessions must be finalized before receipt can be sent
   * - If FREE and not finalized, triggers summary submission + finalization
   * - Waits for finalization to complete before sending receipt
   * 
   * @param {boolean} isAutoSend - True if auto-sending, false if manual send
   * @param {boolean} forceResend - True to force resend even if already sent
   */
  const sendReceipt = useCallback(async (isAutoSend = false, forceResend = false) => {
    const logPrefix = isAutoSend ? '[AutoSend]' : forceResend ? '[Resend]' : '[Send]';

    // A Test session has no customer receipt (billing.display.receipt).
    if (!offersCustomerReceipt(serverSession)) {
      console.log(`${logPrefix} Skipped: no customer receipt for this session`);
      return;
    }
    
    // Validation 1: sessionUid
    if (!state.sessionUid) {
      console.error(`${logPrefix} ✗ Cannot send - missing sessionUid`);
      if (!isAutoSend) {
        showToast({
          message: 'Session identifier not found. Please contact support.',
          type: 'error'
        });
      }
      return;
    }

    // Validation 2: email - use memoized resolved contact
    // ✅ FIX: Use memoized resolvedReceiptContact instead of calling function
    const receiptInfo = resolvedReceiptContact;
    
    // 🔍 COMPREHENSIVE DEBUG LOGGING
    console.log(`${logPrefix} ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    console.log(`${logPrefix} 🔍 RECEIPT SEND DEBUG - Session State:`);
    console.log(`${logPrefix}   sessionUid: ${state.sessionUid}`);
    console.log(`${logPrefix}   stationId: ${state.stationId}`);
    console.log(`${logPrefix}   hasReceiptInfo: ${!!state.receiptInfo}`);
    console.log(`${logPrefix}   receiptInfo.email: ${state.receiptInfo?.email || 'null'}`);
    console.log(`${logPrefix}   receiptInfo.fullName: ${state.receiptInfo?.fullName || 'null'}`);
    console.log(`${logPrefix}   hasVehicle: ${!!state.vehicle}`);
    console.log(`${logPrefix}   vehicle.verified: ${state.vehicle?.verified || false}`);
    console.log(`${logPrefix}   vehicle.ownerEmail: ${state.vehicle?.ownerEmail || 'null'}`);
    console.log(`${logPrefix}   vehicle.ownerName: ${state.vehicle?.ownerName || 'null'}`);
    console.log(`${logPrefix}   vehicle.vehicleId: ${state.vehicle?.vehicleId || 'null'}`);
    console.log(`${logPrefix}   vehicle.billingMode: ${state.vehicle?.billingMode || 'null'}`);
    console.log(`${logPrefix} ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    
    if (!receiptInfo?.email) {
      console.error(`${logPrefix} ✗ Cannot send - missing receipt email`);
      console.error(`${logPrefix} ✗ Resolved receiptInfo:`, receiptInfo);
      if (!isAutoSend) {
        showToast({
          message: 'No receipt email on file.',
          type: 'error'
        });
      }
      setReceiptStatus('failed');
      return;
    }

    // Log complete send context with resolved values
    console.log(`${logPrefix} ✅ Resolved recipient from fallback chain:`, {
      email: receiptInfo.email,
      fullName: receiptInfo.fullName,
      source: 'resolveReceiptContact()'
    });
    
    console.log(`${logPrefix} 📤 Sending receipt with payload:`, {
      sessionUid: state.sessionUid,
      chargerId: state.publicId || state.stationId,
      email: receiptInfo.email,
      fullName: receiptInfo.fullName,
      stationName: state.stationMeta?.name,
      energyKWh: energyDeliveredKWh,
      captureStatus,
      endTime: state.live?.endTime,
      force: forceResend
    });
    
    setReceiptStatus('sending');
    
    try {
      // ═══════════════════════════════════════════════════════════
      // CRITICAL FIX: FREE SESSION FINALIZATION BEFORE RECEIPT SEND
      // ═══════════════════════════════════════════════════════════
      // Priority: Use backend billing_mode from serverSession (authoritative)
      // Fallback: Infer from vehicle.billingMode or pricing
      const billingMode = serverSession?.billing_mode || state.vehicle?.billingMode || 'PUBLIC_CARD';
      const isFreeSession = billingMode === 'FREE' || 
                           (RATE_PER_KWH === 0 && SESSION_FEE === 0);
      
      // Check if FREE session is finalized
      const isFreeFinalized = serverSession?.finalized_at || serverSession?.is_finalized === true;
      
      if (isFreeSession) {
        console.log(`[FreeReceiptFix] ${logPrefix} ═══════════════════════════════════════════════`);
        console.log(`[FreeReceiptFix] ${logPrefix} FREE session detected`);
        console.log(`[FreeReceiptFix] ${logPrefix} billing_mode=${billingMode} (source: ${serverSession?.billing_mode ? 'backend' : 'fallback'})`);
        console.log(`[FreeReceiptFix] ${logPrefix} rate=${RATE_PER_KWH}, fee=${SESSION_FEE}`);
        console.log(`[FreeReceiptFix] ${logPrefix} finalized_at=${serverSession?.finalized_at || 'NULL'}`);
        console.log(`[FreeReceiptFix] ${logPrefix} is_finalized=${serverSession?.is_finalized}`);
        console.log(`[FreeReceiptFix] ${logPrefix} captureStatus=${captureStatus}`);
        
        // If FREE session NOT finalized, trigger finalization flow first
        if (!isFreeFinalized) {
          console.log(`[FreeReceiptFix] ${logPrefix} ⚠️ FREE session NOT finalized - triggering summary/finalization first`);
          
          try {
            // Step 1: Submit summary (which triggers FREE finalization on backend)
            const avgKw = energyDeliveredKWh > 0 && state.live?.startTime && state.live?.endTime
              ? (energyDeliveredKWh / ((new Date(state.live.endTime) - new Date(state.live.startTime)) / 3600000))
              : 0;
            
            const durationSeconds = state.live?.startTime && state.live?.endTime
              ? Math.floor((new Date(state.live.endTime) - new Date(state.live.startTime)) / 1000)
              : 0;
            
            const finalSummary = {
              energyKwh: energyDeliveredKWh,
              avgKw: avgKw,
              durationSeconds: durationSeconds,
              totalCost: submittedTotalCost,
              currency: state.currency || 'BBD',
              startedAt: state.live?.startTime,
              endedAt: state.live?.endTime,
              ratePerKwh: RATE_PER_KWH,
              sessionFee: SESSION_FEE
            };
            
            console.log(`[FreeReceiptFix] ${logPrefix} 📤 Submitting summary to trigger finalization:`, finalSummary);
            const summaryResult = await submitSessionSummary(state.sessionUid, finalSummary);
            
            if (summaryResult.ok) {
              console.log(`[FreeReceiptFix] ${logPrefix} ✅ Summary submitted, finalized=${summaryResult.finalized || false}`);
              
              // Step 2: Refresh session to get finalized fields
              console.log(`[FreeReceiptFix] ${logPrefix} 🔄 Refreshing session to confirm finalization...`);
              const refreshResult = await getCustomerSession(state.sessionUid);
              
              if (refreshResult.ok && refreshResult.session) {
                const refreshedFinalized = refreshResult.session.finalized_at || refreshResult.session.is_finalized === true;
                
                console.log(`[FreeReceiptFix] ${logPrefix} 📥 Session refreshed:`);
                console.log(`[FreeReceiptFix] ${logPrefix}   - finalized_at: ${refreshResult.session.finalized_at || 'NULL'}`);
                console.log(`[FreeReceiptFix] ${logPrefix}   - is_finalized: ${refreshResult.session.is_finalized}`);
                console.log(`[FreeReceiptFix] ${logPrefix}   - payment_status: ${refreshResult.session.payment?.status}`);
                
                if (refreshedFinalized) {
                  console.log(`[FreeReceiptFix] ${logPrefix} ✅ FREE session now finalized - proceeding with receipt send`);
                  setServerSession(refreshResult.session);
                  setCaptureStatus('captured'); // Update capture status
                } else {
                  console.error(`[FreeReceiptFix] ${logPrefix} ❌ FREE session STILL not finalized after summary submission`);
                  console.error(`[FreeReceiptFix] ${logPrefix} Backend finalization may have failed - aborting receipt send`);
                  
                  if (!isAutoSend) {
                    showToast({
                      message: 'Session is still being finalized. Please wait a moment and try again.',
                      type: 'warning'
                    });
                  }
                  return;
                }
              } else {
                console.error(`[FreeReceiptFix] ${logPrefix} ❌ Failed to refresh session after summary:`, refreshResult.error);
                
                if (!isAutoSend) {
                  showToast({
                    message: 'Unable to verify session status. Please try again.',
                    type: 'error'
                  });
                }
                return;
              }
            } else {
              console.error(`[FreeReceiptFix] ${logPrefix} ❌ Failed to submit summary:`, summaryResult.error);
              
              if (!isAutoSend) {
                showToast({
                  message: 'Unable to finalize session. Please contact support.',
                  type: 'error'
                });
              }
              return;
            }
          } catch (finalizationError) {
            console.error(`[FreeReceiptFix] ${logPrefix} ❌ Exception during FREE finalization:`, finalizationError);
            
            if (!isAutoSend) {
              showToast({
                message: 'An error occurred while finalizing session. Please try again.',
                type: 'error'
              });
            }
            return;
          }
        } else {
          console.log(`[FreeReceiptFix] ${logPrefix} ✅ FREE session already finalized - proceeding with receipt send`);
        }
        
        // Mark as paid if not already (legacy path - should be redundant now)
        if (captureStatus === 'not_required') {
          console.log(`[FreeReceiptFix] ${logPrefix} 🔧 Marking FREE session as paid (belt-and-suspenders)`);
          
          try {
            const markResult = await markFreeSessionAsPaid(state.sessionUid);
            
            if (markResult.ok) {
              console.log(`[FreeReceiptFix] ${logPrefix} ✅ FREE session marked as paid`);
              setCaptureStatus('captured');
            } else {
              console.warn(`[FreeReceiptFix] ${logPrefix} ⚠️ Failed to mark as paid (may already be paid):`, markResult.error);
            }
          } catch (markError) {
            console.error(`[FreeReceiptFix] ${logPrefix} ❌ Exception marking as paid:`, markError);
          }
        }
        
        console.log(`[FreeReceiptFix] ${logPrefix} ═══════════════════════════════════════════════`);
      }

      // ══════════════════════════════════════════════════════════════════
      // ENERGY PATCH: Ensure backend has correct energy before receipt send
      // The backend generates receipt content from its own session record.
      // getFinalSessionEnergy() falls back to state.live.energyKWh when the
      // server has 0/null — so the screen shows the right value, but the
      // backend email template uses its stored 0.
      // Fix: if backend is missing energy but we have it locally, patch it first.
      // ══════════════════════════════════════════════════════════════════
      const serverHasEnergy = (serverSession?.final_energy_kwh > 0) ||
                              (serverSession?.summary?.energy_kwh > 0) ||
                              (serverSession?.summary_energy_kwh > 0) ||
                              (serverSession?.energy_kwh > 0);

      if (!serverHasEnergy && energyDeliveredKWh > 0) {
        console.log(`${logPrefix} ⚡ Backend missing energy (server=0/null, local=${energyDeliveredKWh} kWh) — patching before receipt send`);
        try {
          const avgKw = state.live?.startTime && state.live?.endTime
            ? (energyDeliveredKWh / ((new Date(state.live.endTime) - new Date(state.live.startTime)) / 3600000))
            : 0;
          const durationSeconds = state.live?.startTime && state.live?.endTime
            ? Math.floor((new Date(state.live.endTime) - new Date(state.live.startTime)) / 1000)
            : 0;

          await submitSessionSummary(state.sessionUid, {
            energyKwh: energyDeliveredKWh,
            avgKw: avgKw,
            durationSeconds: durationSeconds,
            totalCost: submittedTotalCost,
            currency: state.currency || 'BBD',
            startedAt: state.live?.startTime,
            endedAt: state.live?.endTime,
            ratePerKwh: RATE_PER_KWH,
            sessionFee: SESSION_FEE,
          });
          console.log(`${logPrefix} ✅ Energy patched to backend: ${energyDeliveredKWh} kWh`);
        } catch (patchErr) {
          console.warn(`${logPrefix} ⚠️ Failed to patch energy (non-blocking):`, patchErr);
          // Don't abort the receipt send — backend may still have partial data
        }
      } else {
        console.log(`${logPrefix} ✓ Backend already has energy: ${serverSession?.final_energy_kwh ?? serverSession?.summary?.energy_kwh ?? serverSession?.energy_kwh ?? 0} kWh`);
      }

      // 🔍 DEBUG: Log exact request parameters
      const requestParams = {
        sessionId: state.sessionUid,
        chargerId: state.publicId || state.stationId,
        fullName: receiptInfo.fullName,
        email: receiptInfo.email,
        force: forceResend
      };
      
      console.log(`${logPrefix} 🌐 Calling sendReceiptEmail() with:`, requestParams);
      
      const result = await sendReceiptEmail(requestParams);
      
      // 🔍 DEBUG: Log response
      console.log(`${logPrefix} 📥 Response from sendReceiptEmail():`, {
        success: result.success,
        error: result.error,
        alreadySent: result.alreadySent,
        resent: result.resent,
        correlationId: result.correlationId
      });
      
      if (result.success) {
        setReceiptStatus('sent');
        setReceiptEmail(receiptInfo.email);
        
        if (!isAutoSend) {
          // Manual send toast
          showToast({
            message: result.resent ? 
              'Receipt sent again to ' + receiptInfo.email :
              'Receipt sent to ' + receiptInfo.email,
            type: 'success'
          });
        } else {
          // Auto-send: optional subtle toast
          console.log(`${logPrefix} ✓ Receipt auto-sent successfully to ${receiptInfo.email}`);
          // Uncomment below to show auto-send toast:
          // showToast({
          //   message: 'Receipt sent to ' + receiptInfo.email,
          //   type: 'success',
          //   duration: 3000
          // });
        }
      } else {
        console.error(`${logPrefix} Send failed:`, result.error);
        setReceiptStatus('failed');
        
        if (!isAutoSend) {
          showToast({
            message: result.error || 'Failed to send receipt. Please try again.',
            type: 'error'
          });
        }
      }
    } catch (error) {
      console.error(`${logPrefix} Send exception:`, error);
      setReceiptStatus('failed');
      
      if (!isAutoSend) {
        showToast({
          message: 'An error occurred while sending receipt.',
          type: 'error'
        });
      }
    }
  }, [state.sessionUid, state.stationId, state.stationMeta, state.live, state.vehicle?.billingMode, energyDeliveredKWh, captureStatus, resolvedReceiptContact, showToast, RATE_PER_KWH, SESSION_FEE, serverSession, submittedTotalCost]);

  // Timeout mechanism for graceful degradation
  // If receipt preparation takes too long (60s), EXIT the sync loop and allow manual retry
  // This fixes the infinite receipt sync loop when backend never reaches final state
  //
  // CRITICAL FIX: Use a ref guard (not deps) to start the timer exactly ONCE.
  // Previously, serverSession/loadingSession were in deps — every 2s poll returned a
  // new serverSession object, the effect cleaned up (clearing the timeout), and re-ran
  // (starting a new 60s timeout). The timer NEVER reached 60s.
  // Now the timer starts once and runs uninterrupted. If receipt becomes ready before
  // 60s, the separate check below clears it.
  const preparingTimerStartedRef = useRef(false);

  useEffect(() => {
    // CASE 1: Receipt is ready or already sent — clear any running timer
    if (isReceiptDataReady || receiptStatus !== 'idle') {
      if (preparingTimeoutRef.current) {
        console.log('[Receipt] Clearing timeout - receipt ready or status changed');
        clearTimeout(preparingTimeoutRef.current);
        preparingTimeoutRef.current = null;
      }
      if (isReceiptDataReady) {
        setPreparingTimedOut(false);
        preparingTimerStartedRef.current = false;
      }
      return;
    }

    // CASE 2: Already timed out — nothing to do
    if (preparingTimedOut) return;

    // CASE 3: Timer already running — don't restart it
    if (preparingTimerStartedRef.current) return;

    // CASE 4: No contact info yet — wait for it
    if (!receiptDisplayInfo.hasContact) return;

    // Start the 60s timer exactly once
    preparingTimerStartedRef.current = true;
    console.log('[Receipt] Starting 30s timeout for receipt preparation (timer will NOT reset on poll)');

    preparingTimeoutRef.current = setTimeout(() => {
      console.warn('[Receipt] Receipt preparation timed out after 30s');
      console.warn('[Receipt] EXITING SYNC LOOP - Backend finalization appears stuck');
      setPreparingTimedOut(true);
      // Phase 4 / F4: NEVER force captureStatus='captured' for payment-required
      // billing modes (PUBLIC_CARD, DISCOUNTED_CARD). Doing so would falsely
      // unblock receipt send when the backend has not actually captured payment,
      // and the backend would correctly reject with SESSION_NOT_FINALIZED.
      // Only FREE/FLEET_INVOICE may unblock here.
      const paymentRequired = state.vehicle?.paymentRequired !== false;
      if (!paymentRequired) {
        setCaptureStatus(prev => {
          if (prev !== 'captured' && prev !== 'not_required') {
            console.warn('[Receipt] FREE/FLEET degraded mode: forcing captureStatus to captured');
            return 'captured';
          }
          return prev;
        });
      } else {
        console.warn('[Receipt] PUBLIC_CARD degraded mode: captureStatus left unchanged (receipt send remains blocked until backend finalizes)');
      }
    }, 30000); // 30 seconds — fast enough that users won't refresh and reset the timer

    return () => {
      if (preparingTimeoutRef.current) {
        clearTimeout(preparingTimeoutRef.current);
        preparingTimeoutRef.current = null;
      }
    };
  }, [isReceiptDataReady, preparingTimedOut, receiptStatus, receiptDisplayInfo.hasContact, state.vehicle?.paymentRequired]);

  // ============================================================
  // FINALIZATION DETECTION (split into two effects to avoid race condition)
  // Effect A: reacts to serverSession changes (initial fetch, SSE, poll updates)
  // Effect B: runs the polling interval (stable deps, won't get killed by state updates)
  // ============================================================
  // EFFECT A: Detect if session is already finalized (from initial fetch or SSE update)
  // Separated from polling to avoid dependency-triggered cleanup killing the interval
  useEffect(() => {
    if (captureStatus !== 'idle' && captureStatus !== 'capturing') return;
    if (!isSessionTerminalReady(serverSession)) return;

    console.log('[FINALIZE] ✅ Session terminal-ready (detected from serverSession update):', {
      finalized_at: serverSession.finalized_at,
      final_total_cost: serverSession.final_total_cost,
      status: serverSession.status,
      payment_status: serverSession.payment?.status,
      // Surface which path qualified so we can trace completed-without-finalized_at cases
      via: serverSession.finalized_at || serverSession.is_finalized === true
        ? 'finalized_flag'
        : 'completed_with_data'
    });

    const paymentRequired = state.vehicle?.paymentRequired !== false;
    setCaptureStatus(paymentRequired ? 'captured' : 'not_required');
    // Prefer payment_amount (actual card charge, may be capped at preauth), fallback to final_total_cost
    // Use ?? (nullish) not || (falsy) — $0.00 is a valid captured amount for voided/free sessions
    setCapturedAmount(
      serverSession.payment?.payment_amount
      ?? serverSession.final_total_cost
      ?? serverSession.summary?.total_cost
      ?? serverSession.summary_total_cost
    );
  }, [
    serverSession?.finalized_at,
    serverSession?.is_finalized,
    serverSession?.status,
    serverSession?.final_total_cost,
    serverSession?.summary?.total_cost,
    serverSession?.summary_total_cost,
    serverSession?.stopped_at,
    serverSession?.summary?.ended_at,
    serverSession?.summary_ended_at,
    state.vehicle?.paymentRequired
  ]);

  // Ref guard for Effect B — resets in cleanup so React Strict Mode re-mount works
  const finalizePollActiveRef = useRef(false);

  // EFFECT B: Poll for finalization if not already finalized
  // captureStatus intentionally excluded from deps to prevent cleanup killing the interval
  useEffect(() => {
    // Already polling (same mount cycle)
    if (finalizePollActiveRef.current) return;
    // Already resolved by Effect A (e.g. initial fetch found finalized session)
    if (captureStatus === 'captured' || captureStatus === 'not_required') return;
    if (!state.sessionUid || !state.live?.endTime) return;

    finalizePollActiveRef.current = true;
    console.log('[FINALIZE] Waiting for server-side finalization...');
    setCaptureStatus('capturing');

    let cancelled = false;
    let retryAttempted = false;
    let pollCount = 0;

    const pollInterval = setInterval(async () => {
      if (cancelled) return;
      pollCount++;

      try {
        const result = await getCustomerSession(state.sessionUid);
        if (!result.ok || !result.session || cancelled) return;

        const session = result.session;

        // SUCCESS: Session is in a terminal state with enough data to receipt.
        // Accept finalized_at OR status='completed'/'stopped' with cost+endedAt.
        // Without the broadened check, sessions that the backend parks at
        // 'completed' (without ever setting finalized_at) would keep the
        // 2s poll spinning forever.
        if (isSessionTerminalReady(session)) {
          console.log('[FINALIZE] ✅ Poll detected terminal-ready session:', {
            finalized_at: session.finalized_at,
            status: session.status,
            final_total_cost: session.final_total_cost,
            payment_status: session.payment?.status || session.payment_status,
            via: session.finalized_at || session.is_finalized === true
              ? 'finalized_flag'
              : 'completed_with_data'
          });

          setServerSession(session);
          clearInterval(pollInterval);
          return;
        }

        // Update serverSession with latest data even if not finalized (keeps UI fresh)
        setServerSession(session);

        // DETECT STUCK: finalize_error present OR stuck at 'completed' for too long
        const hasError = !!session.finalize_error;
        const stuckTooLong = pollCount >= 8; // ~16 seconds with no progress

        if ((hasError || stuckTooLong) && !retryAttempted) {
          retryAttempted = true;

          // Normally skip retrigger for non-finalizable statuses (preparing, pending, authorized)
          // because these sessions never started charging and the backend will reject with invalid_status.
          // EXCEPTION: if we received a StopTransaction (state.live.endTime + meter_stop are set),
          // charging DID happen — the backend session is stale/stuck. Attempt retrigger anyway;
          // the backend will succeed if it received the StopTransaction, or return a safe error.
          const nonFinalizableStatuses = ['preparing', 'pending', 'authorized'];
          const hasStopTransactionEvidence = !!(state.live?.endTime && state.live?.meter_stop);
          if (nonFinalizableStatuses.includes(session.status) && !hasStopTransactionEvidence) {
            console.warn('[FINALIZE] Session stuck in non-finalizable status:', session.status, '— no StopTransaction evidence, waiting for timeout degradation');
          } else if (nonFinalizableStatuses.includes(session.status) && hasStopTransactionEvidence) {
            console.warn('[FINALIZE] Session stuck in non-finalizable status:', session.status, '— but StopTransaction received (meter_stop:', state.live.meter_stop, ') — attempting retrigger');
            try {
              const retryResult = await retriggerFinalization(state.sessionUid);
              console.log('[FINALIZE] Retrigger result:', retryResult.ok ? 'success' : retryResult.error);
            } catch (retryErr) {
              console.warn('[FINALIZE] Retrigger failed:', retryErr.message);
            }
          } else {
            console.warn('[FINALIZE] Finalization stuck, triggering retry:', {
              finalize_error: session.finalize_error,
              status: session.status,
              pollCount,
              stuckTooLong
            });

            try {
              const retryResult = await retriggerFinalization(state.sessionUid);
              console.log('[FINALIZE] Retry result:', retryResult.ok ? 'success' : retryResult.error);
            } catch (retryErr) {
              console.warn('[FINALIZE] Retry call failed:', retryErr.message);
            }
          }
        }
      } catch (err) {
        console.warn('[FINALIZE] Poll error:', err.message);
      }
    }, 2000);

    // Timeout after 90 seconds
    const timeout = setTimeout(() => {
      if (cancelled) return;
      console.warn('[FINALIZE] Finalization polling timed out after 90s');
      clearInterval(pollInterval);
      // Phase 4 / F4: do not force captureStatus='captured' for payment-required
      // billing modes. The backend has not confirmed capture; lying here would
      // unblock receipt send and the backend would correctly reject. FREE/FLEET
      // sessions remain free to advance via the existing degraded path.
      const paymentRequired = state.vehicle?.paymentRequired !== false;
      if (!paymentRequired) {
        setCaptureStatus('captured');
      } else {
        console.warn('[FINALIZE] PUBLIC_CARD timeout: captureStatus left unchanged (receipt send remains blocked until backend finalizes)');
      }
    }, 90000);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      clearTimeout(timeout);
      // Reset ref so Strict Mode re-mount (or future mount) can restart polling
      finalizePollActiveRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- captureStatus excluded to keep interval alive
  }, [state.sessionUid, state.live?.endTime, state.vehicle?.paymentRequired]);

  /**
   * Auto-send receipt ONCE when all data is ready (with delay)
   * Waits for payment capture and all session data to hydrate
   * Prevents sending receipts with incomplete information
   * Uses localStorage guard to prevent duplicate auto-sends
   */
  useEffect(() => {
    // Skip if we've already auto-sent for this sessionUid (in-memory guard)
    if (autoSentForSessionRef.current === state.sessionUid) {
      return;
    }

    // Skip if no sessionUid
    if (!state.sessionUid) {
      return;
    }

    // Skip if data not ready yet — log the specific missing fields ONCE
    // per change in the missing-set, not every render. Without this guard
    // each poll tick produces a duplicate "Waiting for data to hydrate"
    // entry, which is what made this loop look stuck.
    if (!isReceiptDataReady) {
      const missing = [];
      if (!state.sessionUid) missing.push('sessionUid');
      if (!state.stationMeta?.name && !state.stationId) missing.push('station');
      if (!resolvedReceiptContact?.email) missing.push('email');
      if (captureStatus !== 'captured' && captureStatus !== 'not_required') {
        missing.push(`capture(${captureStatus})`);
      }
      if (!isSessionTerminalReady(serverSession)) {
        missing.push(`backend(${serverSession?.status || 'no_session'})`);
      }
      const signature = missing.join(',') || 'unknown';
      if (hydrationLogRef.current !== signature) {
        console.log('[AutoSend] Waiting for data to hydrate. Missing:', signature);
        hydrationLogRef.current = signature;
      }
      return;
    }
    // Reset the dedup signature so a future "not ready" transition logs once.
    hydrationLogRef.current = null;

    // Check localStorage guard - prevent auto-send if already sent
    const AUTO_SEND_KEY = `receipt_auto_sent::${state.sessionUid}`;
    const alreadyAutoSent = localStorage.getItem(AUTO_SEND_KEY);
    
    if (alreadyAutoSent) {
      console.log('[AutoSend] Receipt already auto-sent for session:', state.sessionUid);
      setReceiptStatus('sent');
      // ✅ FIX: Use memoized resolvedReceiptContact
      if (resolvedReceiptContact?.email) {
        setReceiptEmail(resolvedReceiptContact.email);
      }
      autoSentForSessionRef.current = state.sessionUid;
      return;
    }

    // Final check: ensure we have email (should already be checked in isReceiptDataReady)
    // ✅ FIX: Use memoized resolvedReceiptContact instead of calling function
    if (!resolvedReceiptContact?.email) {
      console.warn('[AutoSend] Data ready but email missing - skipping auto-send');
      autoSentForSessionRef.current = state.sessionUid;
      return;
    }

    // ALL CHECKS PASSED - Schedule auto-send with delay
    // Random delay between 5-10 seconds to avoid thundering herd at scale
    const delayMs = 5000 + Math.random() * 5000; // 5000-10000ms
    const delaySec = (delayMs / 1000).toFixed(1);
    
    console.log('[AutoSend] ✓ All data hydrated, scheduling auto-send for session:', state.sessionUid, {
      email: resolvedReceiptContact.email,
      stationName: state.stationMeta?.name,
      energyKWh: energyDeliveredKWh,
      captureStatus,
      endTime: state.live?.endTime,
      delay: `${delaySec}s`
    });

    // Mark as processed for this sessionUid to prevent duplicate sends
    autoSentForSessionRef.current = state.sessionUid;

    // Schedule auto-send with random delay between 5-10 seconds
    autoSendTimeoutRef.current = setTimeout(() => {
      console.log('[AutoSend] ⏰ Delay elapsed, sending receipt...');
      
      // Send the receipt (not forced - first send)
      sendReceipt(true, false).then(() => {
        // Set localStorage guard ONLY after successful send
        try {
          localStorage.setItem(AUTO_SEND_KEY, Date.now().toString());
          console.log('[AutoSend] ✓ Set localStorage guard:', AUTO_SEND_KEY);
        } catch (error) {
          console.error('[AutoSend] Failed to set localStorage guard:', error);
        }
      });
    }, delayMs);

    // Cleanup timeout on unmount
    return () => {
      if (autoSendTimeoutRef.current) {
        console.log('[AutoSend] Clearing auto-send timeout (component unmounting)');
        clearTimeout(autoSendTimeoutRef.current);
        autoSendTimeoutRef.current = null;
      }
    };
  }, [
    state.sessionUid,
    isReceiptDataReady,
    captureStatus,
    energyDeliveredKWh,
    state.stationMeta?.name,
    state.live?.endTime,
    resolvedReceiptContact?.email, // ✅ FIX: Narrow dependency - only email field
    sendReceipt
  ]);

  // Inactivity countdown and auto-exit
  useEffect(() => {
    countdownTimerRef.current = setInterval(() => {
      setCountdown(prev => {
        // Never pull the screen away while the driver is typing an address.
        if (captureOpenRef.current) return AUTO_EXIT_SECONDS;
        if (prev <= 1) {
          console.log('[SummaryExit] Countdown reached 0 - auto-exiting to start');
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          handleStartNewSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const events = ['click', 'touchstart', 'keydown', 'scroll', 'mousemove'];
    events.forEach(event => {
      window.addEventListener(event, handleUserInteraction);
    });

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      events.forEach(event => {
        window.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [handleUserInteraction, handleStartNewSession]);

  /**
   * Get payment status display text
   */
  // Fallback only: the payment state shown when the session carries no
  // `billing`. It states whether a payment was needed or taken and never
  // names a session type.
  const paymentStatusText = useMemo(() => {
    if (state.vehicle?.paymentRequired === false) {
      return 'No payment required';
    }

    // Card modes: only the server's capture record may say "Paid" (C-02).
    // The build's payment provider (mock) and a browser-side payment mode
    // are not evidence that money was taken.
    return cardSummaryPaymentLabel(serverSession?.payment);
  }, [serverSession?.payment?.status, serverSession?.payment?.payment_amount, state.vehicle?.paymentRequired]);

  // Billing rows: the backend's `billing.display` on the session, as
  // sent. The driver's summary does not decide what kind of session
  // this was (plan v3.1 I-16 — never claim payment).
  const billingCurrency = serverSession?.payment?.currency || serverSession?.summary?.currency || state?.currency || 'BBD';
  // Without `billing`: the amount the server recorded, as recorded.
  // Until the server has one the row reads "Pending" — never an amount
  // worked out here, and never 0.00.
  const recordedAmountText = recordedCost != null
    ? formatCurrency(recordedCost, billingCurrency)
    : (serverSession && !isSessionFinalized ? 'Pending' : null);
  const billingView = useMemo(() => resolveBillingDisplay(serverSession, {
    amount: recordedAmountText,
    status: { label: paymentStatusText },
  }), [serverSession, recordedAmountText, paymentStatusText]);
  const receiptOffered = offersCustomerReceipt(serverSession);

  // Build actions array based on receipt status
  const actions = useMemo(() => {
    const actionList = [];
    
    // Check if we have email (required for sending receipt)
    const hasEmail = !!resolvedReceiptContact?.email;
    
    if (!receiptOffered) {
      // Test session: no receipt action at all.
    } else if (receiptStatus === 'sending') {
      // Currently sending
      actionList.push({
        id: 'sending-receipt',
        label: 'Sending...',
        type: 'btn-outline-dark',
        disabled: true
      });
    } else if (receiptStatus === 'sent') {
      // Already sent - show "Send again" button
      // ✅ FIX: Enable resend using manual send criteria (doesn't require backend summary)
      actionList.push({
        id: 'send-receipt',
        label: 'Send again',
        type: 'btn-outline-dark',
        disabled: !canManualSendReceipt
      });
    } else if (receiptRequested) {
      actionList.push({
        id: 'receipt-requested',
        label: 'Receipt requested',
        type: 'btn-outline-dark',
        disabled: true
      });
    } else if (!hasEmail) {
      // No address anywhere: this used to be a permanently disabled button,
      // and the CaptureEmail sheet that could collect one was never opened by
      // any screen. 'get-receipt' is handled by ActionButtonWrapper, which
      // opens that sheet; the backend stores the address and queues the one
      // receipt this session is owed.
      actionList.push({
        id: 'get-receipt',
        label: 'Get Receipt',
        type: 'btn-outline-dark'
      });
    } else {
      // ✅ FIX: Enable manual send when criteria are met (email + payment + final data)
      // No longer requires backend summary finalization
      actionList.push({
        id: 'send-receipt',
        label: 'Send Receipt',
        type: 'btn-outline-dark',
        disabled: !canManualSendReceipt
      });
    }
    
    actionList.push({
      id: 'rate-experience',
      label: 'Rate Experience',
      type: 'btn-dark'
    });
    
    actionList.push({
      id: 'start-new-session',
      label: 'Start New Session',
      type: 'btn-outline-dark'
    });
    
    return actionList;
  }, [receiptStatus, receiptRequested, resolvedReceiptContact?.email, canManualSendReceipt, receiptOffered]);

  const handleActionClick = (actionId) => {
    if (actionId === 'start-new-session') {
      handleStartNewSession();
    } else if (actionId === 'send-receipt') {
      // ✅ FIX: Use manual send criteria instead of backend finalization check
      if (!canManualSendReceipt) {
        console.warn('[Send Receipt] Blocked: Manual send criteria not met');
        showToast({
          message: 'Receipt cannot be sent yet. Please ensure payment is complete.',
          type: 'info'
        });
        return;
      }
      
      // Always force resend when clicking manually
      const forceResend = receiptStatus === 'sent';
      sendReceipt(false, forceResend);
    } else if (actionId === 'rate-experience') {
      setShowRatingModal(true);
    }
  };

  const handleCloseRatingModal = () => {
    setShowRatingModal(false);
  };

  // Show loading state while fetching server session
  if (loadingSession) {
    return (
      <div className="ev-content-wrapper session-summary-wrapper">
        <EvHeader
          headerAction={{
            label: 'Charge Summary',
            action: null,
          }}
        />
        <div className="session-summary-inner">
          <div className="session-summary-status-icon">
            {summaryIcon}
          </div>
          <h2 className="session-summary-title">{summaryTitle}</h2>
          <p className="session-summary-subtitle">Finalizing session summary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ev-content-wrapper session-summary-wrapper">
      <EvHeader
        headerAction={{
          label: 'Charge Summary',
          action: null,
        }}
      />

      <div className="session-summary-inner">
        <div className="session-summary-status-icon">
          {summaryIcon}
        </div>

        <h2 className="session-summary-title">{summaryTitle}</h2>
        {/* Show "Finalizing" subtitle when backend hasn't finalized yet
            but we have local data to display. Once finalized, show normal subtitle. */}
        {!isSessionFinalized && captureStatus === 'capturing' ? (
          <p className="session-summary-subtitle">Finalizing session details…</p>
        ) : (
          <p className="session-summary-subtitle">Your charging session has ended.</p>
        )}

        {outcome && (
          <div className="session-summary-canonical">
            <CanonicalSessionPanel snapshot={canonical} compact />
          </div>
        )}

        {receiptStatus === 'sent' && receiptEmail && (
          <p className="receipt-status-message success">
            We emailed your receipt to {receiptEmail}.
          </p>
        )}
        {receiptStatus === 'failed' && (
          <p className="receipt-status-message error">
            We couldn't send your receipt. You can try again.
          </p>
        )}
        {receiptOffered && receiptStatus === 'idle' && !isReceiptDataReady && (() => {
          // ✅ FIX: Show appropriate message based on manual send availability
          if (receiptDisplayInfo.hasContact) {
            // If manual send is available, don't show blocking "Preparing..." message
            if (canManualSendReceipt) {
              return (
                <p className="receipt-status-message info">
                  {receiptDisplayInfo.message}
                </p>
              );
            }
            // Manual send not available yet - show preparing message
            // If timed out but still showing (brief render before re-eval), show degraded message
            if (preparingTimedOut) {
              return (
                <p className="receipt-status-message info">
                  Finalizing receipt...
                </p>
              );
            }
            return (
              <p className="receipt-status-message info">
                Preparing final receipt...
              </p>
            );
          }
          return (
            <p className="receipt-status-message info">
              {receiptDisplayInfo.message}
            </p>
          );
        })()}
        {receiptOffered && receiptStatus === 'idle' && isReceiptDataReady && (() => {
          // ✅ FIX: Use memoized receiptDisplayInfo instead of calling function
          if (receiptDisplayInfo.hasContact) {
            return (
              <p className="receipt-status-message success">
                {receiptDisplayInfo.message}
              </p>
            );
          }
          return (
            <p className="receipt-status-message info">
              {receiptDisplayInfo.message}
            </p>
          );
        })()}

        <InfoPanel>
          <LabelValueRow
            label="Charger"
            value={state?.stationMeta?.name || '—'}
          />
          <LabelValueRow
            label="Location"
            value={state?.stationMeta?.location || '—'}
          />
          <LabelValueRow label="Started" value={startedTime} />
          <LabelValueRow label="Ended" value={endedTime} />
          <LabelValueRow label="Duration" value={duration} />
          <LabelValueRow
            label="Energy Delivered"
            value={formatEnergyKwh(energyDeliveredKWh)}
          />
          {/* Money: the server's stored charge components, then its
              billing rows — exactly as sent. This screen multiplies
              nothing (no "Rate × energy" rows). */}
          <BillingDisplayRows view={billingView} variant="panel" withChargeRows />
          {(() => {
            const currency = serverSession?.payment?.currency || serverSession?.summary?.currency || state?.currency || 'BBD';
            const selectedBudget  = (state.amountCents || 0) / 100;
            const appliedCredit   = state?.appliedCredit ? Number(state.appliedCredit.amount || 0) : 0;
            // The recorded session cost; never a client estimate.
            const finalEnergyCost = Number(recordedCost || 0);

            // Skip if there's nothing useful to say (no budget or no
            // final cost yet — server may still be finalising).
            if (!(selectedBudget > 0) || !(finalEnergyCost > 0)) return null;

            const {
              cardAuthNeeded,
              paidByCredit,
              paidByCard,
              releasedAuthorization,
              hasCredit,
            } = computeReceiptBreakdown({
              selectedBudget, appliedCredit, finalEnergyCost,
            });

            // "You were only charged …" / "Paid by card" describe a card
            // capture: show them only once the server recorded one (C-02).
            // A credit that covered everything charges no card at all.
            if (!serverCapturedCard(serverSession?.payment) && !(hasCredit && paidByCard === 0)) return null;

            // No credit → keep the classic "hold released" copy for
            // parity with the pre-credit baseline. Skip the notice
            // entirely when there's nothing to release (full spend).
            if (!hasCredit) {
              if (releasedAuthorization <= 0) return null;
              return (
                <Notice variant="info" className="hold-release-notice">
                  A pre-authorization hold of {formatCurrency(selectedBudget, currency)} was placed on your card.
                  You were only charged {formatCurrency(paidByCard, currency)} for actual usage.
                  The remaining {formatCurrency(releasedAuthorization, currency)} will be released back to your account within 1–5 business days.
                </Notice>
              );
            }

            // Credit applied → show the full split so the customer
            // sees how their credit + card interacted.
            return (
              <Notice variant="info" className="hold-release-notice hold-release-notice-credit">
                <div className="credit-summary-heading">Payment breakdown</div>
                <dl className="credit-summary-dl">
                  <dt>Selected charging budget</dt>
                  <dd>{formatCurrency(selectedBudget, currency)}</dd>
                  <dt>Charging credit applied</dt>
                  <dd>−{formatCurrency(appliedCredit, currency)}</dd>
                  <dt>Card authorization needed</dt>
                  <dd>{formatCurrency(cardAuthNeeded, currency)}</dd>
                  <dt className="credit-summary-divider">Final energy cost</dt>
                  <dd className="credit-summary-divider">{formatCurrency(finalEnergyCost, currency)}</dd>
                  {paidByCredit > 0 && (<>
                    <dt>Paid by charging credit</dt>
                    <dd>{formatCurrency(paidByCredit, currency)}</dd>
                  </>)}
                  {paidByCard > 0 && (<>
                    <dt>Paid by card</dt>
                    <dd>{formatCurrency(paidByCard, currency)}</dd>
                  </>)}
                  {releasedAuthorization > 0 && (<>
                    <dt>Unused authorization released</dt>
                    <dd>{formatCurrency(releasedAuthorization, currency)}</dd>
                  </>)}
                </dl>
                {releasedAuthorization > 0 && (
                  <p className="credit-summary-note">
                    Released holds return to your account within 1–5 business days.
                  </p>
                )}
                {paidByCard === 0 && (
                  <p className="credit-summary-note">
                    Your card was not charged — your credit covered the full cost.
                  </p>
                )}
              </Notice>
            );
          })()}
        </InfoPanel>


        <div className="auto-exit-countdown">
          Returning to start in {countdown}s
        </div>
      </div>

      <ActionButtonWrapper
        actions={actions}
        onClickAction={handleActionClick}
        onCaptureOpenChange={handleCaptureOpenChange}
        onReceiptRequested={handleReceiptRequested}
      />

      <RatingModal 
        show={showRatingModal} 
        onClose={handleCloseRatingModal}
      />
    </div>
  );
};

export default SessionSummary;
